import { X } from "lucide-react";
import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { SETTINGS_ROUTES } from "../../routes/routes";
import {
  getDialogPropDetails,
  initialScopedProps,
  isDialogOpen,
  setDialog,
} from "../../store/slices/global";
import { contactSupport } from "../../utils/contactSupport";

interface ScopedDialogProps {}
const PREFIX = "ScopedDialogProps";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: "unset",
  },
}));

export const ScopedDialog: FC<ScopedDialogProps> = () => {
  const isOpen = useSelector(isDialogOpen("scoped"));
  const dialogProps = useSelector(getDialogPropDetails("scoped"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleClose = () => {
    dispatch(
      setDialog({
        dialog: "scoped",
        open: false,
        props: initialScopedProps,
      })
    );
  };

  const defaultYesAction = () => {
    void navigate(SETTINGS_ROUTES.Settings);
    dispatch(
      setDialog({ dialog: "scoped", open: false, props: initialScopedProps })
    );
  };

  const defaultNoAction = () => {
    contactSupport();
  };

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={"xs"}
      PaperProps={{
        style: {
          display: !isOpen ? "none" : "flex",
          borderRadius: 15,
          width: "460px",
          height: "260px",
          justifyContent: "center",
          paddingBottom: "35px",
        },
      }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <Grid
          sx={{ paddingBottom: "0px" }}
          container
          direction={"row"}
          justifyContent={"flex-end"}
        >
          <IconButton onClick={handleClose}>
            <X size={16} />
          </IconButton>
        </Grid>
        <DialogTitle id="dialogBox-title" className={classes.dialogTitle}>
          <Grid container item direction={"column"}>
            <Grid item>{dialogProps.title}</Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ width: "346px" }}>
          <IgTypography>{dialogProps.message}</IgTypography>
        </DialogContent>
        {dialogProps.showButtons === false ? null : (
          <DialogActions>
            <IgButton
              onClick={() => {
                if (!dialogProps.noButtonAction) {
                  defaultNoAction();
                } else {
                  dialogProps.noButtonAction();
                }
              }}
              sx={{
                letterSpacing: "0.5px",
                fontSize: "16px",
                fontWeight: 500,
              }}
              variant={"outlined"}
            >
              {dialogProps.noButtonText || "Contact Us"}
            </IgButton>
            <IgButton
              onClick={() => {
                if (!dialogProps.yesButtonAction) {
                  defaultYesAction();
                } else {
                  dialogProps.yesButtonAction();
                }
              }}
              sx={{ letterSpacing: "0.5px", fontSize: "16px", fontWeight: 500 }}
              variant={"contained"}
            >
              {dialogProps.yesButtonText || "View Plans"}
            </IgButton>
          </DialogActions>
        )}
      </Grid>
    </StyledDialog>
  );
};
